import { WorkflowStatusIcon } from "./WorkflowStatusIcon";
import type { UserCard, WorkflowState } from "@/lib/store";

/** Linear-style priority bars: 0 none (dashes), 1 urgent (!), 2-4 bars. */
export function PriorityIcon({ priority, className = "h-4 w-4" }: { priority: number; className?: string }) {
  if (priority === 1) {
    return (
      <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
        <rect x="1" y="1" width="14" height="14" rx="3" className="fill-warning" />
        <path d="M8 4v5M8 11.5v.5" stroke="#1b1b1f" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  const bars = priority === 0 ? 0 : 5 - priority; // high 3, medium 2, low 1
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={2 + i * 4.5}
          y={10 - i * 3.5}
          width="3"
          height={4 + i * 3.5}
          rx="1"
          className={i < bars ? "fill-current" : "fill-current opacity-25"}
          style={priority === 0 ? { opacity: 0.35 } : undefined}
        />
      ))}
    </svg>
  );
}

/** Ring that fills by state type; color from the state. */
export function StateIcon({ state, className }: { state?: WorkflowState; className?: string }) {
  return <WorkflowStatusIcon status={state?.type ?? "backlog"} color={state?.color ?? "#8b8d91"} className={className} />;
}

export function Avatar({ user, size = 20, className = "" }: { user?: UserCard | { name: string; avatar_url?: string | null }; size?: number; className?: string }) {
  if (!user) {
    return (
      <span className={`inline-flex items-center justify-center rounded-full border border-dashed border-border-strong text-faint ${className}`} style={{ width: size, height: size }}>
        <svg viewBox="0 0 16 16" width={size * 0.6} height={size * 0.6} fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="5.5" r="2.5" />
          <path d="M3 13.5c1-2.5 3-3.5 5-3.5s4 1 5 3.5" />
        </svg>
      </span>
    );
  }
  const hue = [...user.name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0);
  return user.avatar_url ? (
    <img src={user.avatar_url} alt="" className={`rounded-full ${className}`} style={{ width: size, height: size }} />
  ) : (
    <span
      className={`inline-flex items-center justify-center rounded-full font-medium uppercase text-white ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.45, background: `hsl(${hue} 45% 45%)` }}
      title={user.name}
    >
      {user.name.slice(0, 1)}
    </span>
  );
}

export function LabelDot({ color, className = "h-2 w-2" }: { color: string; className?: string }) {
  return <span className={`inline-block rounded-full ${className}`} style={{ background: color }} />;
}
